import React from "react";

/* Shared helpers, colours and components for the four rail dashboards. */
export const STATUS_LABEL = {
    draft: "Draft", loading: "Loading",
    in_transit_to_transnet: "In Transit",
    received_at_transnet: "At Yard", staged_at_transnet: "Staged",
    in_transit_to_grindrod: "In Transit",
    received_at_grindrod: "At Grindrod",
    waiting_for_tipping: "Waiting",
    tipping_in_progress: "Tipping",
    completed: "Completed",
    empty_wagons_returning: "Returning",
    empty_wagons_at_transnet: "At Transnet (empty)",
    empty_wagons_in_transit_to_mine: "→ Mine (empty)",
    cycle_complete: "Cycle Complete",
};
export const STATUS_TONE = {
    draft: "slate", loading: "blue",
    in_transit_to_transnet: "green",
    received_at_transnet: "amber", staged_at_transnet: "amber",
    in_transit_to_grindrod: "green",
    received_at_grindrod: "indigo",
    waiting_for_tipping: "indigo",
    tipping_in_progress: "indigo",
    completed: "emerald",
    empty_wagons_returning: "purple",
    empty_wagons_at_transnet: "orange",
    empty_wagons_in_transit_to_mine: "orange",
    cycle_complete: "slate",
};
const TONE_BG = {
    slate: { bg: "#F1F5F9", fg: "#334155" },
    blue:  { bg: "#DBEAFE", fg: "#1E40AF" },
    green: { bg: "#DCFCE7", fg: "#166534" },
    amber: { bg: "#FEF3C7", fg: "#92400E" },
    indigo:{ bg: "#E0E7FF", fg: "#3730A3" },
    emerald:{bg: "#D1FAE5", fg: "#065F46" },
    purple:{ bg: "#EDE9FE", fg: "#5B21B6" },
    orange:{ bg: "#FED7AA", fg: "#9A3412" },
    red:   { bg: "#FEE2E2", fg: "#991B1B" },
};
export const statusPill = (status) => {
    const t = TONE_BG[STATUS_TONE[status]] || TONE_BG.slate;
    return (
        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
              style={{ backgroundColor: t.bg, color: t.fg }}>
            {STATUS_LABEL[status] || status}
        </span>
    );
};
export const fmtDt = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-ZA",
            { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};
export const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("en-ZA",
        { hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
};
export const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-ZA",
        { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
};
export const nowClock = () => new Date().toLocaleTimeString("en-ZA",
    { hour: "2-digit", minute: "2-digit" });
export const todayLabel = () => new Date().toLocaleDateString("en-ZA",
    { day: "2-digit", month: "short", year: "numeric" });

/* ═══════════════════════════════════════════════════════════════════════
 * Pictogram icons — coloured/filled SVG illustrations to match the
 * dashboard story-telling mocks. Every icon has a sensible default
 * palette but exposes `color` (accent) so it can be re-tinted.
 * ═══════════════════════════════════════════════════════════════════════ */

/** Mine head-gear tower with coal pile at base */
export const IcnMine = ({ size = 32, color = "#065F46" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        {/* wooden frame */}
        <path d="M8 34 L20 6 L32 34 Z" fill="#78350F" stroke="#3F2412" strokeWidth="1.2" />
        <path d="M13 34 L20 14 L27 34 Z" fill="#92400E" />
        {/* cross-braces */}
        <line x1="14" y1="22" x2="26" y2="22" stroke="#3F2412" strokeWidth="1.2" />
        <line x1="16" y1="28" x2="24" y2="28" stroke="#3F2412" strokeWidth="1.2" />
        {/* pulley */}
        <circle cx="20" cy="10" r="2" fill="#FBBF24" stroke="#3F2412" strokeWidth="1" />
        {/* coal pile */}
        <path d="M4 34 Q12 30 20 34 Q28 30 36 34 L36 38 L4 38 Z"
              fill={color} opacity="0.85" />
        <path d="M4 38 L36 38" stroke="#0B1A3B" strokeWidth="1" />
    </svg>
);

/** Orange staging yard building with pitched roof + adjacent crane */
export const IcnYard = ({ size = 32, color = "#EA580C" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        {/* roof */}
        <path d="M6 18 L20 8 L34 18 L34 20 L6 20 Z" fill="#7C2D12" />
        {/* body */}
        <rect x="8" y="20" width="24" height="14" fill={color} stroke="#7C2D12" strokeWidth="1.2" />
        {/* doors + windows */}
        <rect x="12" y="24" width="6" height="10" fill="#FED7AA" stroke="#7C2D12" strokeWidth="0.8" />
        <rect x="22" y="24" width="8" height="5" fill="#FED7AA" stroke="#7C2D12" strokeWidth="0.8" />
        <rect x="22" y="30" width="8" height="4" fill="#FED7AA" stroke="#7C2D12" strokeWidth="0.8" />
        <line x1="6" y1="34" x2="34" y2="34" stroke="#3F2412" strokeWidth="1.4" />
    </svg>
);

/** Blue tippler crane */
export const IcnCrane = ({ size = 32, color = "#2563EB" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        {/* mast */}
        <rect x="9" y="6" width="4" height="28" fill={color} stroke="#0B1A3B" strokeWidth="1" />
        {/* boom */}
        <path d="M11 8 L34 8 L34 12 L15 12 Z" fill={color} stroke="#0B1A3B" strokeWidth="1" />
        {/* cable + hook */}
        <line x1="28" y1="12" x2="28" y2="20" stroke="#0B1A3B" strokeWidth="1" />
        <path d="M26 20 h4 v4 h-4 Z" fill="#94A3B8" stroke="#0B1A3B" strokeWidth="1" />
        {/* base + hopper */}
        <rect x="4" y="30" width="32" height="4" fill="#1E3A8A" />
        <rect x="14" y="26" width="16" height="6" fill="#3B82F6" stroke="#0B1A3B" strokeWidth="1" />
    </svg>
);

/** Loaded coal train (locomotive + coal-heap wagon) */
export const IcnTrain = ({ size = 28, color = "#166534" }) => (
    <svg width={size} height={size} viewBox="0 0 40 32" xmlns="http://www.w3.org/2000/svg">
        {/* locomotive body */}
        <rect x="4" y="10" width="16" height="14" rx="2" fill={color} stroke="#0B1A3B" strokeWidth="1" />
        {/* cab window */}
        <rect x="14" y="12" width="4" height="5" fill="#DBEAFE" stroke="#0B1A3B" strokeWidth="0.8" />
        {/* chimney */}
        <rect x="7" y="6" width="3" height="4" fill="#0B1A3B" />
        {/* wagon */}
        <rect x="22" y="14" width="14" height="10" fill="#334155" stroke="#0B1A3B" strokeWidth="1" />
        {/* coal heap */}
        <path d="M22 14 Q29 10 36 14 Z" fill="#111827" />
        {/* wheels */}
        <circle cx="8"  cy="26" r="2.5" fill="#0B1A3B" />
        <circle cx="15" cy="26" r="2.5" fill="#0B1A3B" />
        <circle cx="25" cy="26" r="2.5" fill="#0B1A3B" />
        <circle cx="33" cy="26" r="2.5" fill="#0B1A3B" />
        {/* rail */}
        <line x1="0" y1="30" x2="40" y2="30" stroke="#64748B" strokeWidth="1.2" />
    </svg>
);

/** Empty wagons — flat-top open bogies, no coal */
export const IcnEmpty = ({ size = 28, color = "#7C3AED" }) => (
    <svg width={size} height={size} viewBox="0 0 40 32" xmlns="http://www.w3.org/2000/svg">
        <rect x="3"  y="12" width="15" height="10" fill={color} stroke="#0B1A3B" strokeWidth="1" opacity="0.9" />
        <rect x="22" y="12" width="15" height="10" fill={color} stroke="#0B1A3B" strokeWidth="1" opacity="0.9" />
        {/* empty inside */}
        <rect x="5"  y="14" width="11" height="6" fill="#F3F4F6" />
        <rect x="24" y="14" width="11" height="6" fill="#F3F4F6" />
        {/* wheels */}
        <circle cx="7"  cy="25" r="2.5" fill="#0B1A3B" />
        <circle cx="14" cy="25" r="2.5" fill="#0B1A3B" />
        <circle cx="26" cy="25" r="2.5" fill="#0B1A3B" />
        <circle cx="33" cy="25" r="2.5" fill="#0B1A3B" />
        <line x1="0" y1="29" x2="40" y2="29" stroke="#64748B" strokeWidth="1.2" />
    </svg>
);

export const IcnCheck = ({ size = 26, color = "#065F46" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill={color} />
        <polyline points="7 12 11 16 17 8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const IcnBell = ({ size = 18, color = "white" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

export function Panel({ title, right, children, testid, className = "" }) {
    return (
        <div className={`bg-white rounded-lg border border-slate-200 ${className}`} data-testid={testid}>
            {title && (
                <div className="px-3 md:px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-lg">
                    <div className="text-[11px] font-black uppercase tracking-widest text-[#0B1A3B]">{title}</div>
                    {right}
                </div>
            )}
            <div className="p-3 md:p-4">{children}</div>
        </div>
    );
}
export function KpiTile({ title, value, sub, tint, testid, icon }) {
    return (
        <div className="bg-white rounded-lg border border-slate-200 p-3 relative" data-testid={testid}>
            {tint && <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg" style={{ backgroundColor: tint }} />}
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{title}</div>
                    <div className="text-3xl font-black text-slate-900 mt-1 leading-none">{value}</div>
                    {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
                </div>
                {icon && <div className="opacity-80">{icon}</div>}
            </div>
        </div>
    );
}
export function Field({ label, children, wide }) {
    return (
        <label className={`block ${wide ? "col-span-2" : ""}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 block mb-1">{label}</span>
            {children}
        </label>
    );
}
export function DarkHeader({ title, subtitle, right, tint = "#0B1A3B", icon }) {
    return (
        <div className="rounded-lg text-white p-4 flex items-center justify-between gap-3 flex-wrap"
             style={{ backgroundColor: tint }}>
            <div className="flex items-center gap-3 min-w-0">
                {icon && <div className="flex-shrink-0">{icon}</div>}
                <div className="min-w-0">
                    <h1 className="text-xl md:text-2xl font-black leading-tight uppercase tracking-wide truncate">{title}</h1>
                    {subtitle && <div className="text-xs text-white/70">{subtitle}</div>}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">{right}</div>
        </div>
    );
}
/* ═════════════════════════════════════════════════════════════════════
 * WagonPipelineRibbon — 6-segment horizontal ribbon showing exactly
 * where each wagon of a consignment currently is:
 *   Loaded · At Transnet · In transit · At Grindrod · At Tippler · Tipped
 * Renders inline for any consignment doc. Zero-count segments collapse.
 * ═════════════════════════════════════════════════════════════════════ */
export function WagonPipelineRibbon({ c, compact = false }) {
    if (!c) return null;
    const total = c.wagon_count || 0;
    const atTransnet = c.wagons_at_transnet || 0;
    const dispatched = c.wagons_dispatched_to_grindrod || 0;
    const atGrindrod = c.wagons_at_grindrod || 0;
    const released = c.wagons_released || 0;
    const accepted = c.wagons_accepted_at_tippler || 0;
    const tipped = c.wagons_tipped || 0;

    const buckets = [
        { key: "loaded",       label: "Loaded",      count: Math.max(0, total - atTransnet),               colour: "#94A3B8" },
        { key: "at_transnet",  label: "At Transnet", count: Math.max(0, atTransnet - dispatched),          colour: "#F97316" },
        { key: "in_transit",   label: "In transit",  count: Math.max(0, dispatched - atGrindrod),          colour: "#8B5CF6" },
        { key: "at_grindrod",  label: "At Grindrod", count: Math.max(0, atGrindrod - released),            colour: "#2563EB" },
        { key: "at_tippler",   label: "At Tippler",  count: Math.max(0, released - tipped),                colour: "#F59E0B" },
        { key: "tipped",       label: "Tipped",      count: Math.max(0, tipped),                           colour: "#10B981" },
    ];
    const sum = buckets.reduce((a, b) => a + b.count, 0) || 1;

    return (
        <div className={compact ? "" : "g-card p-3"} data-testid="wagon-pipeline-ribbon">
            {!compact && (
                <div className="flex justify-between items-baseline mb-2">
                    <div className="text-[11px] uppercase tracking-widest font-black text-[var(--grindrod-navy)]">
                        Wagon Pipeline · {c.consignment_no}{c.train_id ? ` · Train ${c.train_id}` : ""}
                    </div>
                    <div className="text-[10px] text-slate-500 font-semibold">
                        {tipped}/{total} tipped · {total - tipped} still in pipeline
                    </div>
                </div>
            )}
            <div className="h-9 rounded-md overflow-hidden flex border border-slate-300 bg-white">
                {buckets.map((b) => {
                    const pct = (b.count / sum) * 100;
                    if (pct === 0) return null;
                    return (
                        <div key={b.key}
                             className="h-full flex items-center justify-center text-white text-[10px] font-black tracking-wide overflow-hidden whitespace-nowrap"
                             style={{ backgroundColor: b.colour, width: `${pct}%` }}
                             title={`${b.label}: ${b.count} wagons`}>
                            {pct >= 8 ? `${b.count} ${b.label}` : b.count}
                        </div>
                    );
                })}
            </div>
            {!compact && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px]">
                    {buckets.map((b) => (
                        <div key={b.key} className="flex items-center gap-1">
                            <span className="inline-block w-2.5 h-2.5 rounded" style={{ backgroundColor: b.colour }} />
                            <span className="uppercase tracking-widest text-slate-600">{b.label}</span>
                            <span className="font-black text-slate-800">{b.count}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ═════════════════════════════════════════════════════════════════════
 * BatchPresetButtons — one-tap presets for common release sizes
 * (10 · 20 · 40 · half · all). max is the ceiling (available pending).
 * Fires onPick(count) — safe against exceeding max.
 * ═════════════════════════════════════════════════════════════════════ */
export function BatchPresetButtons({ max, onPick, testidPrefix = "batch" }) {
    const presets = [10, 20, 40];
    const half = Math.max(1, Math.floor((max || 0) / 2));
    const opts = [
        ...presets.filter((p) => p <= max).map((p) => ({ label: String(p), value: p })),
        ...(max > 1 ? [{ label: "½", value: half }] : []),
        ...(max > 0 ? [{ label: "All", value: max }] : []),
    ];
    if (!opts.length) return null;
    return (
        <div className="flex flex-wrap gap-1 pt-1" data-testid={`${testidPrefix}-presets`}>
            {opts.map((o) => (
                <button
                    key={o.label} type="button"
                    onClick={() => onPick(o.value)}
                    className="px-2 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-[10px] font-bold uppercase tracking-widest transition"
                    data-testid={`${testidPrefix}-preset-${o.label}`}
                    title={`Set count to ${o.value}`}
                >
                    {o.label}
                    {o.label !== "All" && o.label !== "½" ? "" : ` (${o.value})`}
                </button>
            ))}
        </div>
    );
}

/* ═════════════════════════════════════════════════════════════════════
 * ShortfallBanner — polls /rail/shortfalls and shows a red strip
 * whenever a consignment has stalled >4h with wagons upstream. Clicking
 * a row scrolls to that consignment's card (via anchor).
 * ═════════════════════════════════════════════════════════════════════ */
export function ShortfallBanner({ testid = "rail-shortfall-banner" }) {
    const [items, setItems] = React.useState([]);
    React.useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { api } = await import("../../lib/api");
                const r = await api.get("/rail/shortfalls");
                if (alive) setItems(r.data?.items || []);
            } catch { /* silent */ }
        };
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, []);
    if (!items.length) return null;
    return (
        <div className="rounded-md border-2 border-red-400 bg-red-50 p-3 shadow-sm" data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest font-black text-red-800 mb-1">
                ⚠  Shortfall alert — {items.length} consignment{items.length > 1 ? "s" : ""} stalled &gt; 4h
            </div>
            <ul className="text-xs space-y-0.5">
                {items.slice(0, 6).map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-x-2 text-red-900">
                        <b className="font-mono">{c.consignment_no}</b>
                        <span className="text-red-700">· {c.mine_name || "—"}</span>
                        <span className="px-1.5 rounded bg-red-200 text-red-900 text-[9px] font-black uppercase tracking-widest">
                            stalled at {c.stall_stage.replaceAll("_", " ")}
                        </span>
                        <span>{c.stall_hours}h since last update ·</span>
                        <span className="text-red-800">{c.pending} wagons pending upstream</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* Existing exports below ─────────────────────────────────────────── */
export function DateClockChips({ notifBtn }) {
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <div className="px-3 py-1.5 rounded bg-white/10 text-[11px] font-mono">📅 {todayLabel()}</div>
            <div className="px-3 py-1.5 rounded bg-white/10 text-[11px] font-mono">🕒 {nowClock()}</div>
            {notifBtn}
        </div>
    );
}

/* ═════════════════════════════════════════════════════════════════════
 * NotificationBell — bell + unread badge with a dropdown popover
 * listing recent rail notifications. Click any unread row to ack it,
 * or hit "Mark all read". Refreshes on parent `notifs` prop update.
 * ═════════════════════════════════════════════════════════════════════ */
export function NotificationBell({ notifs, onChange, testid = "rail-notif-btn" }) {
    const [open, setOpen] = React.useState(false);
    const items = notifs?.items || [];
    const unread = notifs?.unread_count || 0;
    const rootRef = React.useRef(null);
    const lastSeenIdRef = React.useRef(null);
    const bootstrappedRef = React.useRef(false);

    // Auto-toast on new notification arrivals — one toast per new item.
    // Skip the initial load so users don't get flooded on mount.
    React.useEffect(() => {
        (async () => {
            if (!items.length) return;
            const { toast } = await import("sonner");
            const newestId = items[0].id;
            if (!bootstrappedRef.current) {
                lastSeenIdRef.current = newestId;
                bootstrappedRef.current = true;
                return;
            }
            if (newestId === lastSeenIdRef.current) return;
            // Find every item after the last-seen and toast newest → oldest.
            const cutIdx = items.findIndex((n) => n.id === lastSeenIdRef.current);
            const fresh = cutIdx === -1 ? items.slice(0, 3) : items.slice(0, cutIdx);
            fresh.slice(0, 3).forEach((n) => {
                const label = (n.organisation || "").toUpperCase();
                toast.message(`🚆 ${label} · ${n.message}`, { duration: 6000 });
            });
            lastSeenIdRef.current = newestId;
        })();
    }, [items]);

    React.useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const ackOne = async (nid) => {
        try {
            await import("../../lib/api").then(({ api }) =>
                api.post(`/rail/notifications/${nid}/ack`));
        } catch { /* silent — retry via ack-all */ }
        onChange?.();
    };
    const ackAll = async () => {
        try {
            await import("../../lib/api").then(({ api }) =>
                api.post("/rail/notifications/ack-all"));
        } catch { /* silent */ }
        onChange?.();
    };

    const isUnread = (n) => {
        const rb = n.read_by || [];
        // Whether *this actor* has read it isn't in the payload — use
        // unread_count vs feed heuristic: any notification present in the
        // feed above the unread threshold is treated as unread until acked.
        return rb.length === 0 || unread > 0;
    };

    const fmt = (iso) => {
        if (!iso) return "";
        try { return new Date(iso).toLocaleString("en-ZA", { hour12: false }); }
        catch { return iso; }
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 transition"
                data-testid={testid}
                aria-label={`Notifications ${unread} unread`}
            >
                <IcnBell />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[9px] grid place-items-center font-black">
                        {unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-[360px] max-w-[92vw] max-h-[70vh] overflow-hidden
                                rounded-lg shadow-2xl bg-white border border-slate-200 z-50 text-slate-900"
                     data-testid={`${testid}-popover`}>
                    <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
                        <div className="text-[11px] font-black uppercase tracking-widest text-slate-700">
                            Notifications
                            <span className="ml-2 text-[10px] text-slate-500 font-semibold">
                                {items.length ? `${unread} unread · ${items.length} total` : "no messages"}
                            </span>
                        </div>
                        <button
                            type="button" onClick={ackAll}
                            disabled={unread === 0}
                            className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded
                                       bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-300"
                            data-testid={`${testid}-ack-all`}
                        >Mark all read</button>
                    </div>
                    <div className="overflow-y-auto max-h-[60vh] divide-y divide-slate-100">
                        {items.length === 0 && (
                            <div className="p-6 text-center text-slate-400 text-sm italic">
                                No notifications yet. You'll be paged the moment a state change fires.
                            </div>
                        )}
                        {items.map((n) => {
                            const org = (n.organisation || "").toUpperCase();
                            const kind = n.kind || "";
                            const unreadItem = isUnread(n);
                            return (
                                <button
                                    type="button"
                                    key={n.id}
                                    onClick={() => ackOne(n.id)}
                                    className={`w-full text-left p-3 text-xs hover:bg-slate-50 block
                                        ${unreadItem ? "bg-blue-50/60" : "bg-white"}`}
                                    data-testid={`${testid}-item-${n.id}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        {unreadItem && (
                                            <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                                        )}
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700">
                                            {org}
                                        </span>
                                        {kind && (
                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800">
                                                {kind.replaceAll("_", " ")}
                                            </span>
                                        )}
                                        <span className="ml-auto text-[9px] text-slate-500 font-mono">{fmt(n.created_at)}</span>
                                    </div>
                                    <div className="text-slate-800">{n.message}</div>
                                    {n.consignment_no && (
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            Consignment <b>{n.consignment_no}</b>
                                            {n.train_id ? <> · Train {n.train_id}</> : null}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/* Small step in a horizontal process flow */
export function FlowStep({ label, sub, sub2, active, done, colour = "#2563EB", icon }) {
    const bg = done ? "#DCFCE7" : active ? "#FEF3C7" : "#F1F5F9";
    const border = done ? "#16A34A" : active ? "#F59E0B" : "#CBD5E1";
    return (
        <div className="flex-1 min-w-[120px] rounded-lg border-2 p-2 text-center"
             style={{ backgroundColor: bg, borderColor: border }}>
            {icon && <div className="grid place-items-center mb-1">{icon}</div>}
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-800">{label}</div>
            {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
            {done && <div className="mt-1 text-emerald-600 text-lg leading-none">✓</div>}
            {active && !done && <div className="mt-1 text-amber-600 text-lg leading-none">●</div>}
        </div>
    );
}
export function FlowArrow({ colour = "#94A3B8" }) {
    return (
        <div className="grid place-items-center px-1 text-xl" style={{ color: colour }}>→</div>
    );
}
